// Streaming surface — SSE in production (Phase 1C+).
//
// Wire contract (matches backend src/routes/stream.ts):
//   event: status      data: SessionStatus
//   event: thinking    data: { delta }
//   event: message     data: { text, files? }
//   event: tool        data: { toolName, toolArgs, status, toolResult?, toolId? }
//   event: done        data: {}
//   event: error       data: { message }
//   event: compact     data: CompactionState (native Compact start/finish)
//
// Backend events do NOT include the frontend's messageId — the store
// substitutes `streamingMessageId` for events that arrive without one.
//
// Uses @microsoft/fetch-event-source so we can attach a Firebase JWT
// bearer header (native EventSource is cookie-only) and reconnect with
// Last-Event-ID after backgrounding the tab.

import { fetchEventSource } from "@microsoft/fetch-event-source";
import type { SessionStatus, ToolCall } from "../lib/types";
import type { ScheduledTurnAdmission } from "./scheduledTurnAdmission";
import { validPublicProgress } from "../lib/publicProgressProof";

import type { NativeInputSnapshot } from "./nativeInputs";

export type StreamEvent =
  | { type: "native_input"; data: NativeInputSnapshot & { companyId: string; __seq?: number; __epoch?: number } }
  | { type: "connection"; data: { companyId: string; state: "connected" | "connecting" | "reconnecting" | "auth-required"; reconnected?: boolean; message?: string; resolvedMessage?: string } }
  | { type: "scheduled_turn"; data: ScheduledTurnAdmission & { __seq?: number; __epoch?: number } }
  | { type: "automated_turn"; data: ScheduledTurnAdmission & { kind: string; __seq?: number; __epoch?: number } }
  | { type: "status"; data: SessionStatus }
  | { type: "thinking"; data: { messageId: string; delta: string } }
  | { type: "message_delta"; data: { messageId: string; persistedMessageId?: string; outOfTurn?: true; delta: string; files?: string[]; meta?: import("../lib/types").MessageMeta } }
  | { type: "tool_start"; data: { messageId: string; tool: ToolCall } }
  | { type: "tool_update"; data: { messageId: string; toolId: string; status: ToolCall["status"]; result?: ToolCall["result"]; publicProgress?: ToolCall["publicProgress"] } }
  | { type: "queue"; data: { length: number; processing: boolean } }
  | { type: "done"; data: { messageId: string } }
  | { type: "compact"; data: import("../lib/types").CompactionState }
  | { type: "error"; data: { message: string } };

export type StreamDispatcher = (e: StreamEvent) => void;

export interface StreamSubscription {
  close(): void;
}

class FatalSSEError extends Error {}

export function connectStream(
  companyId: string,
  dispatch: StreamDispatcher,
  getToken?: (forceRefresh?: boolean) => Promise<string | null>,
): StreamSubscription {
  const ctrl = new AbortController();
  let closed = false;
  // Per-message tool state: maps tool_id (from backend) → ensures we know
  // when to emit tool_start vs tool_update.
  const seenToolIds = new Set<string>();
  let hasOpened = false;
  let connectionState = "";
  let connectionMessage: string | undefined;
  const connection = (state: "connected" | "connecting" | "reconnecting" | "auth-required", message?: string) => {
    if (closed || connectionState === state) return;
    const resolvedMessage = state === "connected" ? connectionMessage : undefined;
    connectionState = state;
    connectionMessage = message;
    dispatch({ type: "connection", data: { companyId, state, message, resolvedMessage, reconnected: hasOpened } });
  };
  const ensureOpen = (signal?: AbortSignal | null) => {
    if (closed || ctrl.signal.aborted || signal?.aborted) throw new DOMException("Stream closed", "AbortError");
  };
  // fetch-event-source keeps Last-Event-ID across physical connections. Supply
  // a fetch adapter, not a new subscription, so only the bearer is refreshed.
  const authenticatedFetch: typeof fetch = async (input, init) => {
    const request = async (forceRefresh: boolean) => {
      ensureOpen(init?.signal);
      if (!getToken) throw new FatalSSEError("Sign in to reconnect live updates. Your work may still be running.");
      const token = await getToken(forceRefresh);
      ensureOpen(init?.signal);
      if (!token) throw new FatalSSEError("Sign in to reconnect live updates. Your work may still be running.");
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    };
    let response = await request(false);
    if (response.status === 401) {
      // One force-refresh retry per connection; persistent rejection terminates
      // cleanly instead of hammering auth or falsely labelling the task failed.
      try { await response.body?.cancel(); } catch { /* best-effort release */ }
      response = await request(true);
    }
    return response;
  };

  void (async () => {
    try {
      ensureOpen();
      await fetchEventSource(
        `/api/sessions/${encodeURIComponent(companyId)}/stream`,
        {
          signal: ctrl.signal,
          fetch: authenticatedFetch,
          openWhenHidden: true,
          async onopen(res) {
            if (res.ok && res.headers.get("content-type")?.includes("text/event-stream")) {
              connection("connected");
              hasOpened = true;
              return;
            }
            if (res.status === 401 || res.status === 403) {
              throw new FatalSSEError("Sign in to reconnect live updates. Your work may still be running.");
            }
            throw new Error(`SSE open failed: HTTP ${res.status}`);
          },
          onmessage(ev) {
            if (closed) return;
            const eventName = ev.event || "message";
            let data: any = {};
            if (ev.data) {
              try { data = JSON.parse(ev.data); } catch { /* keep {} */ }
            }
            // Wave 4 P0-3: pass the SSE `id:` (broadcaster seq) through to
            // the reducer via __seq on every event's data. The reducer uses
            // it for idempotent replay — Mode-2 (no Last-Event-ID) replay
            // re-delivers events the frontend already saw, which would
            // double-append content/thinking deltas without dedup. The seq
            // is monotonic per company-epoch and resets on backend restart.
            const seq = ev.id ? Number(ev.id) : 0;
            // Per-company epoch — backend stamps it on every event. The store
            // resets its seq-dedup tracker when this increases (epoch change =
            // backend restart / session respawn), which fixes the stuck-render
            // / lingering-Stop after an SSE reconnect lands on a fresh epoch.
            const epoch = Number(data.__epoch ?? 0);
            switch (eventName) {
              case "automated_turn": {
                const uuid = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
                const requestId = typeof data.message?.id === "string" ? data.message.id : "";
                const validId = new RegExp("^auto-" + uuid + "$", "i").test(requestId) ||
                  data.kind === "Scheduled Message" && new RegExp("^" + uuid + "$", "i").test(requestId);
                if (validId && ["Auto Revision", "Approved Revision", "Feature", "Problem", "Scheduled Message"].includes(data.kind) &&
                    data.companyId === companyId && data.message?.companyId === companyId &&
                    data.message.role === "user" && typeof data.message.content === "string" &&
                    data.attempt === requestId && Number.isFinite(Date.parse(data.startedAt)) &&
                    Number.isFinite(Date.parse(data.message.createdAt))) {
                  dispatch({ type: "automated_turn", data: { ...data, __seq: seq, __epoch: epoch } });
                }
                break;
              }
              case "scheduled_turn":
                if (data.companyId === companyId && data.message?.companyId === companyId &&
                    data.message?.role === "user" && typeof data.message.id === "string" &&
                    (data.message.id.startsWith("sch_") || data.message.id.startsWith("run_sv2_")) && typeof data.message.content === "string" &&
                    typeof data.attempt === "string" && data.attempt &&
                    Number.isFinite(Date.parse(data.startedAt)) && Number.isFinite(Date.parse(data.message.createdAt))) {
                  dispatch({ type: "scheduled_turn", data: { ...data, __seq: seq, __epoch: epoch } });
                }
                break;
              case "native_input":
                dispatch({ type: "native_input", data: { ...data, companyId, __seq: seq, __epoch: epoch } });
                break;
              case "status":
                dispatch({ type: "status", data: { ...data, __seq: seq, __epoch: epoch } as any });
                break;
              case "thinking":
                dispatch({ type: "thinking", data: { messageId: "", delta: String(data.delta ?? ""), __seq: seq, __epoch: epoch } as any });
                break;
              case "message":
                dispatch({
                  type: "message_delta",
                  data: {
                    messageId: "",
                    persistedMessageId: typeof data.messageId === "string" && data.messageId ? data.messageId : undefined,
                    // The backend's own verdict: the CLI's follow-up after its
                    // turn ended. Never routed into a newer turn's placeholder.
                    ...(data.outOfTurn === true ? { outOfTurn: true } : {}),
                    delta: String(data.text ?? ""),
                    // Carry the reply's attachments so they render LIVE (the
                    // `reply` tool's files[] — e.g. snap screenshots, generated
                    // PDFs). Previously dropped, so reply attachments only
                    // appeared after a reload — AND a files-only / empty-text
                    // reply left the streaming placeholder "empty", which trips
                    // the done-handler's skip-if-empty guard → lingering Stop +
                    // stalled queue. Surfacing files here fixes both.
                    files: Array.isArray(data.files) ? data.files : undefined,
                    // Response footer (type/effort/model/duration). Optional sibling of
                    // text/files — the reducer stamps it without touching content.
                    meta: data.meta,
                    __seq: seq,
                    __epoch: epoch,
                  } as any,
                });
                break;
              case "tool": {
                const publicProgress=validPublicProgress(data.publicProgress,companyId);
                const status: ToolCall["status"] =
                  data.status === "ok" || data.status === "error" ? data.status : "running";
                const toolId = String(data.toolId ?? `tool-${Date.now()}`);
                if (status === "running" || !seenToolIds.has(toolId)) {
                  seenToolIds.add(toolId);
                  dispatch({
                    type: "tool_start",
                    data: {
                      messageId: "",
                      tool: {
                        id: toolId,
                        toolName: String(data.toolName ?? "Tool"),
                        args: (data.toolArgs ?? {}) as Record<string, unknown>,
                        status,
                        result: data.toolResult,
                        ...(publicProgress ? {publicProgress} : {}),
                      },
                      __seq: seq, __epoch: epoch,
                    } as any,
                  });
                } else {
                  dispatch({
                    type: "tool_update",
                    data: {
                      messageId: "",
                      toolId,
                      status,
                      result: data.toolResult,
                      ...(publicProgress ? {publicProgress} : {}),
                      __seq: seq, __epoch: epoch,
                    } as any,
                  });
                }
                break;
              }
              case "compact":
                // Native Compact start/finish (server-owned state).
                if (typeof data.state === "string") dispatch({ type: "compact", data: { ...data, __seq: seq, __epoch: epoch } as unknown as import("../lib/types").CompactionState });
                break;
              case "done":
                dispatch({ type: "done", data: { messageId: "", __seq: seq, __epoch: epoch } as any });
                seenToolIds.clear();
                break;
              case "error":
                dispatch({ type: "error", data: { message: String(data.message ?? "stream error"), __seq: seq, __epoch: epoch } as any });
                break;
              case "queue":
                dispatch({
                  type: "queue",
                  data: { length: Number(data.length ?? 0), processing: !!data.processing, __seq: seq, __epoch: epoch } as any,
                });
                break;
              default:
                // Unknown event — ignore.
                break;
            }
          },
          onerror(err) {
            // Throwing rethrows and stops reconnect; returning a number = backoff ms.
            if (err instanceof FatalSSEError) throw err;
            // A first connection has nothing to disconnect from. Keep normal
            // opening quiet; a failed initial attempt is a connection retry,
            // while a dropped established stream is genuinely reconnecting.
            connection(
              hasOpened ? "reconnecting" : "connecting",
              // Same text as delivery.ts LIVE_* (the store debounces these).
              hasOpened
                ? "Live updates disconnected. Reconnecting; your work may still be running."
                : "Connecting to live updates. Retrying…",
            );
            return 1500;
          },
          onclose() {
            // Server told us to close; reconnect.
            if (!closed) throw new Error("server closed");
          },
        },
      );
    } catch (err: any) {
      if (closed) return;
      connection("auth-required", String(err?.message ?? "Live updates unavailable. Check your sign-in."));
    }
  })();

  return {
    close() {
      closed = true;
      ctrl.abort();
    },
  };
}

// Phase 1A mock leftover — kept exported as a no-op so any import path
// doesn't break. Real streams come from the backend now.
export function emitMockReply(_messageId: string, _userText: string): void {
  /* no-op in Phase 1C+ */
}

export function abortMockStream(): void {
  /* no-op in Phase 1C+ */
}
